import { useEffect, useState } from "react";
import { FileSignature } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalPendingContracts } from "../../features/portal/portal-slice";
import {
  acceptPortalContract,
  fetchPortalPendingContracts,
} from "../../features/portal/slice-extensions/portal-slice-extension";
import { PortalContractResponse } from "../../features/portal/contracts/PortalContractResponse";
import { formatMoney } from "../../features/billing/utils/money";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

/**
 * Contracts awaiting the member's (or guardian's) online acceptance: full terms,
 * an explicit consent checkbox, and one Accept button per contract. Renders
 * nothing when no acceptance is pending — most visits never see it.
 */
const PortalContractAcceptanceCard = ({ memberId }: { memberId: string }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const pending = useAppSelector(selectPortalPendingContracts(memberId));
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPortalPendingContracts(memberId));
  }, [dispatch, memberId]);

  if (!pending || pending.length === 0) {
    return null;
  }

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(`${date}T00:00:00`),
    );

  const describeTerms = (contract: PortalContractResponse) => {
    if (contract.packSize != null) {
      return t("portal.acceptance.packTerms", "{{count}} visits", { count: contract.packSize });
    }
    if (contract.monthlyFeeAmountMinorUnits != null && contract.monthlyFeeCurrency) {
      const fee = `${formatMoney(contract.monthlyFeeAmountMinorUnits, contract.monthlyFeeCurrency, getDisplayLocale())} / ${t("portal.month", "month")}`;
      return contract.cadence ? `${fee} · ${contract.cadence}` : fee;
    }
    return null;
  };

  const accept = async (contractId: string) => {
    setError(null);
    setBusyId(contractId);
    try {
      await dispatch(acceptPortalContract({ memberId, contractId })).unwrap();
    } catch (e) {
      setError(
        (e as { message?: string })?.message ??
          t("portal.acceptance.error", "The contract could not be accepted right now."),
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="with-border mb-4 rounded-2xl bg-white p-3 dark:bg-primary-20">
      <p className="flex items-center gap-2 font-medium text-grey-20 dark:text-white">
        <FileSignature size={16} className="shrink-0 text-grey-30 dark:text-grey-70" />
        {t("portal.acceptance.title", "Contract awaiting your acceptance")}
      </p>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "portal.acceptance.intro",
          "Please review the terms below. Questions? Just ask your instructor before accepting.",
        )}
      </p>
      {error && <p className="mt-2 text-sm font-medium text-red-700 dark:text-red-400">{error}</p>}

      <ul className="mt-3 flex flex-col gap-3">
        {pending.map((contract) => (
          <li key={contract.id} className="rounded-xl border border-grey-90 p-3 dark:border-primary-10">
            <p className="font-medium text-grey-20 dark:text-white">
              {t(`portal.contractType.${contract.type}`, contract.type)}
            </p>
            <p className="text-sm text-grey-40 dark:text-grey-80">
              {describeTerms(contract)}
            </p>
            <p className="text-sm text-grey-40 dark:text-grey-80">
              {t("portal.acceptance.validity", "Valid from {{from}}", { from: formatDate(contract.validFrom) })}
              {contract.validUntil
                ? ` ${t("portal.acceptance.validUntil", "until {{until}}", { until: formatDate(contract.validUntil) })}`
                : ""}
            </p>
            <label className="mt-2 flex items-start gap-2 text-sm text-grey-20 dark:text-white">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={agreed[contract.id] ?? false}
                onChange={(e) => setAgreed((a) => ({ ...a, [contract.id]: e.target.checked }))}
              />
              <span>{t("portal.acceptance.agree", "I have read the terms and accept this contract.")}</span>
            </label>
            <button
              type="button"
              className="btn btn-primary mt-2"
              disabled={!agreed[contract.id] || busyId === contract.id}
              onClick={() => accept(contract.id)}
            >
              {t("portal.acceptance.accept", "Accept contract")}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PortalContractAcceptanceCard;
